import React, { useEffect, useState } from "react";
import { AiOutlineArrowRight, AiOutlineBarChart } from "react-icons/ai";

const PLATFORM_LABELS = {
  youtube: "YouTube",
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
  reddit: "Reddit",
  x_twitter: "X / Twitter",
  linkedin: "LinkedIn",
  pinterest: "Pinterest",
  snapchat: "Snapchat",
  threads: "Threads",
  whatsapp: "WhatsApp",
  meta: "Meta",
};

const number = (value) => Number(value || 0).toLocaleString();

export default function OnboardingSourcePreview() {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    fetch("/api/admin/onboarding-analytics", { credentials: "same-origin", cache: "no-store" })
      .then(async (response) => {
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || "Unable to load source analytics.");
        return result;
      })
      .then((result) => { if (active) setAnalysis(result); })
      .catch((requestError) => { if (active) setError(requestError.message || "Unable to load source analytics."); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, []);

  const platforms = analysis?.sourceAnalysis?.socialPlatforms || [];
  const topPlatforms = platforms.slice(0, 4);
  const sourceAnalysis = analysis?.sourceAnalysis || {};

  return (
    <section className="admin-card admin-source-preview p-5" aria-labelledby="onboarding-source-preview-heading" aria-busy={loading}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-sky-600"><AiOutlineBarChart /> Onboarding analytics</p>
          <h2 id="onboarding-source-preview-heading" className="mt-1 text-lg font-bold text-slate-950">Where new accounts come from</h2>
          <p className="mt-1 text-sm text-slate-500">A quick look at tracked social signups and survey mentions.</p>
        </div>
        <a href="/admin/onboarding-analytics" className="admin-button admin-button-secondary">
          View insights <AiOutlineArrowRight />
        </a>
      </div>

      {error ? <p role="alert" className="mt-4 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p> : loading ? (
        <div className="mt-5 grid animate-pulse gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-label="Loading social source summary">
          {[1, 2, 3, 4].map((item) => <div key={item} className="h-20 rounded-lg bg-slate-100" />)}
        </div>
      ) : (
        <>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {topPlatforms.length ? topPlatforms.map((platform) => (
              <article key={platform.value} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <h3 className="font-semibold text-slate-900">{PLATFORM_LABELS[platform.value] || platform.value}</h3>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500">Tracked</p>
                    <p className="text-xl font-bold text-sky-700">{number(platform.tracked)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Survey</p>
                    <p className="text-xl font-bold text-indigo-700">{number(platform.reported)}</p>
                  </div>
                </div>
              </article>
            )) : <p className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500 sm:col-span-2 lg:col-span-4">No social sources have been recorded yet.</p>}
          </div>
          <p className="mt-3 text-xs text-slate-500">
            {number(sourceAnalysis.trackedCoverage)} of {number(analysis?.summary?.initialized)} new accounts have a UTM source or referrer. Tracked and survey counts can overlap; these summarize signups.
          </p>
        </>
      )}
    </section>
  );
}
